/**
 * Canvas描画のためのJavaScript Interop関数（TypeScript版）
 */

import {TextLayout, StrapDimensions, EngraveSettings, ValidationResult} from '../types';
import {TextLayoutService} from './textLayoutService';

/**
 * Canvas Interop クラス
 */
class CanvasInterop {
    // SVG画像のキャッシュ（パスごとに管理）
    private svgImages: Map<string, HTMLImageElement> = new Map();
    private svgImagesLoading: Map<string, boolean> = new Map();
    private svgImageCallbacks: Map<string, Array<{
        resolve: (img: HTMLImageElement) => void;
        reject: (error: any) => void;
    }>> = new Map();

    // 彫刻後の色（レーザー彫刻後の色）
    private readonly ENGRAVED_COLOR = '#EDFFBE';
    private readonly ENGRAVED_R = 0xED;
    private readonly ENGRAVED_G = 0xFF;
    private readonly ENGRAVED_B = 0xBE;

    // プレビュー表示の飽和係数（調整可能）
    // 値が大きいほど両端で急激に飽和する。
    private readonly SATURATION_K = 3.0;

    /**
     * フォントを読み込む
     * @param fontFamily フォント名
     * @param fontSize フォントサイズ
     * @param isBold 太字かどうか
     * @param isItalic 斜体かどうか
     */
    private async loadFont(fontFamily: string, fontSize: number, isBold: boolean, isItalic: boolean): Promise<void> {
        try {
            // まず、すべてのフォントが読み込まれるまで待つ
            await document.fonts.ready;

            // フォントスタイル文字列を構築
            const fontStyle = isItalic ? 'italic' : 'normal';
            const fontWeight = isBold ? 'bold' : 'normal';
            const fontString = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;

            // 特定のフォントを読み込む
            await document.fonts.load(fontString);

            // フォントのレンダリングが完全に完了するまで待つ
            await new Promise(resolve => setTimeout(resolve, 50));
        } catch (error) {
            console.warn('フォントの読み込みに失敗しました:', error);
            // エラーが発生してもデフォルトフォントで続行
        }
    }

    /**
     * プレビューをCanvasに描画（新しいバージョン：TypeScript側で計算）
     * @param canvas Canvas要素
     * @param settings 彫刻設定
     * @param strap ストラップ寸法情報
     * @param svgPath SVG画像のパス（デフォルト: '/Butter_front_minify.svg'）
     * @returns バリデーション結果
     */
    async renderPreviewWithLayout(
        canvas: HTMLCanvasElement,
        settings: EngraveSettings,
        strap: StrapDimensions,
        svgPath: string = '/Butter_front_minify.svg'
    ): Promise<ValidationResult> {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Canvas 2Dコンテキストの取得に失敗しました');
            return {isValid: false, errors: ['Canvas 2Dコンテキストの取得に失敗しました'], warnings: []};
        }

        // Canvasを完全にクリア（状態もリセット）
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        // SVG背景を描画（非同期）
        await this.drawSvgBackground(ctx, strap, svgPath);

        // 画像モードの場合
        if (settings.isImageMode && settings.imageData) {
            return await this.drawGrayscaleImage(ctx, settings.imageData, strap);
        }

        // テキストモードの場合
        // フォントを確実に読み込む
        await this.loadFont(settings.fontFamily, settings.fontSize, settings.isBold, settings.isItalic);

        // TextLayoutServiceを毎回新しく作成（Canvas状態を確実にリセット）
        const layoutService = new TextLayoutService(ctx);

        // レイアウト計算
        const layout = layoutService.calculateLayout(settings, strap);

        // テキストを描画
        this.drawText(ctx, layout, strap);

        // バリデーション
        const validationResult = layoutService.validateLayout(layout, strap);

        return validationResult;
    }

    /**
     * プレビューをCanvasに描画（従来のバージョン：C#側で計算済み）
     * @param canvas Canvas要素
     * @param layout テキストレイアウト情報
     * @param strap ストラップ寸法情報
     * @param svgPath SVG画像のパス（デフォルト: '/Butter_front_minify.svg'）
     */
    async renderPreview(canvas: HTMLCanvasElement, layout: TextLayout, strap: StrapDimensions, svgPath: string = '/Butter_front_minify.svg'): Promise<void> {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Canvas 2Dコンテキストの取得に失敗しました');
            return;
        }

        // Canvasをクリア
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // SVG背景を描画（非同期）
        await this.drawSvgBackground(ctx, strap, svgPath);

        // テキストを描画
        this.drawText(ctx, layout, strap);
    }

    /**
     * SVG画像を読み込む（キャッシュ付き）
     * @param svgPath SVG画像のパス
     * @returns Promise<HTMLImageElement>
     */
    private loadSvgImage(svgPath: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            // 既に読み込み済みの場合
            const cachedImage = this.svgImages.get(svgPath);
            if (cachedImage) {
                resolve(cachedImage);
                return;
            }

            // 読み込み中の場合はコールバックリストに追加
            if (this.svgImagesLoading.get(svgPath)) {
                const callbacks = this.svgImageCallbacks.get(svgPath) || [];
                callbacks.push({resolve, reject});
                this.svgImageCallbacks.set(svgPath, callbacks);
                return;
            }

            // 読み込み開始
            this.svgImagesLoading.set(svgPath, true);
            const img = new Image();

            img.onload = () => {
                this.svgImages.set(svgPath, img);
                this.svgImagesLoading.set(svgPath, false);

                // コールバックを実行
                const callbacks = this.svgImageCallbacks.get(svgPath) || [];
                callbacks.forEach(cb => cb.resolve(img));
                this.svgImageCallbacks.delete(svgPath);

                resolve(img);
            };

            img.onerror = (error) => {
                this.svgImagesLoading.set(svgPath, false);
                console.error('SVG画像の読み込みに失敗しました:', error);

                // コールバックを実行
                const callbacks = this.svgImageCallbacks.get(svgPath) || [];
                callbacks.forEach(cb => cb.reject(error));
                this.svgImageCallbacks.delete(svgPath);

                reject(error);
            };

            img.src = svgPath;
        });
    }

    /**
     * SVG背景を描画
     * @param ctx Canvas 2Dコンテキスト
     * @param strap ストラップ寸法情報
     * @param svgPath SVG画像のパス
     */
    private async drawSvgBackground(ctx: CanvasRenderingContext2D, strap: StrapDimensions, svgPath: string): Promise<void> {
        try {
            const img = await this.loadSvgImage(svgPath);

            // SVGの実際のサイズ（ブラウザが認識するnaturalサイズ）を使用
            const svgWidth = img.naturalWidth;
            const svgHeight = img.naturalHeight;

            // Canvas上での描画先サイズ
            const destX = 0;
            const destY = 0;
            const destWidth = strap.canvasWidth;
            const destHeight = strap.canvasHeight;

            // SVG全体をCanvasに描画
            ctx.drawImage(
                img,
                0, 0, svgWidth, svgHeight,                    // ソース矩形
                destX, destY, destWidth, destHeight           // 描画先矩形
            );
        } catch (error) {
            console.error('SVG背景の描画に失敗しました:', error);
            // エラーの場合は従来の手描き背景にフォールバック
            this.drawStrapBackground(ctx, strap);
        }
    }

    /**
     * グレースケール画像を描画（レーザー彫刻風）
     * @param ctx Canvas 2Dコンテキスト
     * @param imageDataUrl base64エンコードされた画像データURL
     * @param strap ストラップ寸法情報
     * @returns バリデーション結果
     */
    private async drawGrayscaleImage(
        ctx: CanvasRenderingContext2D,
        imageDataUrl: string,
        strap: StrapDimensions
    ): Promise<ValidationResult> {
        try {
            // 画像を読み込む
            const img = await this.loadImage(imageDataUrl);

            // 彫刻可能領域の位置を計算（textLayoutService.tsと同じロジック）
            // mm→px変換係数をstrapから計算（ズームレベルに応じて自動調整）
            const mmToPx = strap.canvasWidth / strap.widthMm;
            const headHeightPx = Math.round(18.1 * mmToPx); // バターの頭部分の高さ

            const engravableWidth = strap.engravableWidthPx;
            const engravableHeight = strap.engravableHeightPx;
            const engravableStartX = (strap.canvasWidth - engravableWidth) / 2;
            const engravableStartY = headHeightPx + (strap.canvasHeight - headHeightPx - engravableHeight) / 2;

            // 彫刻可能領域の中心点
            const engravableCenterX = engravableStartX + engravableWidth / 2;
            const engravableCenterY = engravableStartY + engravableHeight / 2;

            // 一時的なCanvasを作成して画像を処理
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            const tempCtx = tempCanvas.getContext('2d');

            if (!tempCtx) {
                return {
                    isValid: false,
                    errors: ['Canvas 2Dコンテキストの取得に失敗しました'],
                    warnings: []
                };
            }

            // 画像を一時Canvasに描画
            tempCtx.drawImage(img, 0, 0);

            // ピクセルデータを取得
            const imageData = tempCtx.getImageData(0, 0, img.width, img.height);
            const data = imageData.data;

            // グレースケール画像を処理
            // 黒色部分（彫刻対象）を白色（彫刻後の色）に変換
            // 黒の濃さに応じて白色の強度を変化させる
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const originalAlpha = data[i + 3]; // 元のアルファ値を取得

                // 元のアルファ値が0（完全に透明）の場合はスキップ
                if (originalAlpha === 0) {
                    data[i] = 0;
                    data[i + 1] = 0;
                    data[i + 2] = 0;
                    data[i + 3] = 0;
                    continue;
                }

                // グレースケール値（0=黒、255=白）
                const gray = (r + g + b) / 3;

                // 黒の濃さを計算（0=白、1=黒）- tanh関数で両端を飽和
                // 薄い部分はより薄く、濃い部分はより濃くなるS字カーブ
                const linearIntensity = 1 - (gray / 255);
                const t = linearIntensity * 2 - 1;  // 0〜1 を -1〜1 に変換
                const blackIntensity = (Math.tanh(this.SATURATION_K * t) + 1) / 2;

                // 黒の濃さに応じて彫刻色を適用
                // 完全な白（gray=255）の場合は透明、完全な黒（gray=0）の場合は彫刻色
                data[i] = this.ENGRAVED_R;
                data[i + 1] = this.ENGRAVED_G;
                data[i + 2] = this.ENGRAVED_B;
                // 元のアルファ値と黒の濃さの両方を考慮
                data[i + 3] = Math.round(blackIntensity * originalAlpha);
            }

            // 処理済み画像データを一時Canvasに戻す
            tempCtx.putImageData(imageData, 0, 0);

            // 画像の物理サイズ（mm）
            const IMAGE_WIDTH_MM = 20.1;
            const IMAGE_HEIGHT_MM = 40.6;
            const MM_TO_INCH = 25.4;

            // 現在のDPIを計算（ズームレベルに応じて自動調整）
            const currentDpi = (strap.canvasWidth / strap.widthMm) * MM_TO_INCH;

            // 画像のCanvas上での目標サイズ（ピクセル）を計算
            const targetWidthPx = Math.round(IMAGE_WIDTH_MM * currentDpi / MM_TO_INCH);
            const targetHeightPx = Math.round(IMAGE_HEIGHT_MM * currentDpi / MM_TO_INCH);

            // 画像の描画位置を計算（画像の中心が彫刻可能領域の中心と一致するように）
            const imageDrawX = engravableCenterX - targetWidthPx / 2;
            const imageDrawY = engravableCenterY - targetHeightPx / 2;

            // メインCanvasに描画
            ctx.save();

            // 合成方法を設定（通常合成）
            // 加算合成（'lighter'）も試せるが、通常合成の方が自然な見た目になる
            ctx.globalCompositeOperation = 'source-over';

            // 画像をDPIを考慮してスケーリングして描画
            // 異なるDPIの画像でも、Canvas上では同じ物理サイズ（20.1×40.6mm）で表示される
            ctx.drawImage(
                tempCanvas,
                0, 0, img.width, img.height,        // ソース画像全体
                imageDrawX, imageDrawY,              // 描画位置
                targetWidthPx, targetHeightPx        // 目標サイズ（DPI調整済み）
            );

            ctx.restore();

            return {
                isValid: true,
                errors: [],
                warnings: []
            };
        } catch (error) {
            console.error('グレースケール画像の描画に失敗しました:', error);
            return {
                isValid: false,
                errors: [`グレースケール画像の描画に失敗しました: ${error instanceof Error ? error.message : String(error)}`],
                warnings: []
            };
        }
    }

    /**
     * 画像を読み込む
     * @param dataUrl base64エンコードされた画像データURL
     * @returns Promise<HTMLImageElement>
     */
    private loadImage(dataUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => {
                resolve(img);
            };

            img.onerror = () => {
                reject(new Error('画像の読み込みに失敗しました'));
            };

            img.src = dataUrl;
        });
    }

    /**
     * ストラップ背景を描画（3Dプリント素材風）
     * @param ctx Canvas 2Dコンテキスト
     * @param strap ストラップ寸法情報
     */
    private drawStrapBackground(ctx: CanvasRenderingContext2D, strap: StrapDimensions): void {
        const width = strap.canvasWidth;
        const height = strap.canvasHeight;

        // バターの頭部分（黄色）の高さ
        const headHeight = 173;
        const bodyTop = headHeight;

        // 角丸の半径
        const cornerRadius = 15;

        // 1. バターの頭部分（黄色）を描画
        this.drawRoundedRect(ctx, 0, 0, width, headHeight, cornerRadius, '#F4D03F');

        // 2. バターの体部分（緑色）を描画
        this.drawRoundedRect(ctx, 0, bodyTop, width, height - bodyTop, cornerRadius, '#7FB069');

        // 3. 顔を描画
        const faceY = headHeight / 2;
        const centerX = width / 2;

        ctx.fillStyle = '#000000';

        // 目
        const eyeY = faceY - 8;
        const eyeSpacing = 8;
        const eyeRadius = 2.5;

        // 左目
        ctx.beginPath();
        ctx.arc(centerX - eyeSpacing, eyeY, eyeRadius, 0, Math.PI * 2);
        ctx.fill();

        // 右目
        ctx.beginPath();
        ctx.arc(centerX + eyeSpacing, eyeY, eyeRadius, 0, Math.PI * 2);
        ctx.fill();

        // 口
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const mouthY = faceY + 8;
        const mouthWidth = 12;
        const mouthDepth = 4;

        ctx.beginPath();
        ctx.moveTo(centerX - mouthWidth / 2, mouthY);
        ctx.lineTo(centerX, mouthY + mouthDepth);
        ctx.lineTo(centerX + mouthWidth / 2, mouthY);
        ctx.stroke();

        // 4. 腕を描画
        const armY = bodyTop + 30;
        const armLength = 50;
        const armWidth = 8;
        const handWidth = 12;
        const handHeight = 18;

        ctx.fillStyle = '#7FB069';
        ctx.strokeStyle = '#6A9A5B';
        ctx.lineWidth = 1;

        // 左腕
        ctx.beginPath();
        ctx.moveTo(0, armY);
        ctx.lineTo(-armLength + 5, armY);
        ctx.lineWidth = armWidth;
        ctx.lineCap = 'round';
        ctx.stroke();

        // 左手
        ctx.beginPath();
        ctx.ellipse(-armLength, armY, handWidth, handHeight, Math.PI / 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // 右腕
        ctx.beginPath();
        ctx.moveTo(width, armY);
        ctx.lineTo(width + armLength - 5, armY);
        ctx.lineWidth = armWidth;
        ctx.stroke();

        // 右手
        ctx.beginPath();
        ctx.ellipse(width + armLength, armY, handWidth, handHeight, -Math.PI / 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // 5. 足を描画
        const footTop = height;
        const footLength = 40;
        const footWidth = 8;
        const shoeWidth = 16;
        const shoeHeight = 10;
        const legSpacing = 20;

        ctx.fillStyle = '#C74444';
        ctx.strokeStyle = '#A03333';
        ctx.lineWidth = 1;

        // 左足
        ctx.beginPath();
        ctx.moveTo(centerX - legSpacing, footTop);
        ctx.lineTo(centerX - legSpacing - 10, footTop + footLength - shoeHeight);
        ctx.lineWidth = footWidth;
        ctx.lineCap = 'round';
        ctx.stroke();

        // 左靴
        ctx.beginPath();
        ctx.ellipse(centerX - legSpacing - 10, footTop + footLength, shoeWidth, shoeHeight, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // 右足
        ctx.beginPath();
        ctx.moveTo(centerX + legSpacing, footTop);
        ctx.lineTo(centerX + legSpacing + 10, footTop + footLength - shoeHeight);
        ctx.lineWidth = footWidth;
        ctx.stroke();

        // 右靴
        ctx.beginPath();
        ctx.ellipse(centerX + legSpacing + 10, footTop + footLength, shoeWidth, shoeHeight, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // 6. 彫刻可能領域の境界線（SVGの緑色塗りつぶし領域と同じサイズ）
        const engravableWidth = strap.engravableWidthPx;
        const engravableHeight = strap.engravableHeightPx;
        const engravableX = (width - engravableWidth) / 2;
        const engravableY = bodyTop + (height - bodyTop - engravableHeight) / 2;

        ctx.strokeStyle = '#DC143C';
        ctx.lineWidth = 2;
        ctx.strokeRect(engravableX, engravableY, engravableWidth, engravableHeight);

        // 7. 微妙な影効果
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(width - 8, 0, 8, height);
        ctx.fillRect(0, height - 8, width, 8);
    }

    /**
     * 角丸長方形を描画
     */
    private drawRoundedRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
        fillColor: string
    ): void {
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
    }

    /**
     * テキストを描画（レーザー彫刻風）
     * @param ctx Canvas 2Dコンテキスト
     * @param layout テキストレイアウト情報
     * @param _strap ストラップ寸法情報（将来の使用のために保持）
     */
    private drawText(ctx: CanvasRenderingContext2D, layout: TextLayout, strap: StrapDimensions): void {
        if (!layout.positions || layout.positions.length === 0) {
            return;
        }

        // フォントスタイルを設定
        const fontStyle = this.buildFontStyle(layout, strap);
        ctx.font = fontStyle;
        ctx.textBaseline = 'top';

        // テキストの色（レーザー彫刻後の色）
        ctx.fillStyle = this.ENGRAVED_COLOR;

        // 縦書きの場合は90度回転として扱う
        const rotationDegrees = layout.isVertical ? 90 : 0;
        const rotationRadians = rotationDegrees * Math.PI / 180;

        // 回転がある場合は、テキストブロックの中心を回転の中心とする
        if (rotationRadians !== 0) {
            // テキストブロックのバウンディングボックスを計算
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    const metrics = ctx.measureText(position.text);
                    // actualBoundingBoxを使用して実際のグリフの境界を取得
                    const actualLeft = metrics.actualBoundingBoxLeft;
                    const actualRight = metrics.actualBoundingBoxRight;
                    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
                    const yOffset = -metrics.actualBoundingBoxAscent;

                    // 実際のグリフの左端と右端を使用
                    minX = Math.min(minX, position.x - actualLeft);
                    minY = Math.min(minY, position.y + yOffset);
                    maxX = Math.max(maxX, position.x + actualRight);
                    maxY = Math.max(maxY, position.y + yOffset + textHeight);
                }
            });

            // ユーザー指定の目標位置（回転なしの状態での左上端）
            const targetX = minX;
            const targetY = minY;

            // テキストブロックの中心点を計算
            let textCenterX = (minX + maxX) / 2;
            let textCenterY = (minY + maxY) / 2;

            // 回転後のAABBを計算するために、各行の4つの角を回転変換
            let aabbMinX = Infinity, aabbMinY = Infinity;
            let aabbMaxX = -Infinity, aabbMaxY = -Infinity;

            const cos = Math.cos(rotationRadians);
            const sin = Math.sin(rotationRadians);

            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    const metrics = ctx.measureText(position.text);
                    // actualBoundingBoxを使用して実際のグリフの境界を取得
                    const actualLeft = metrics.actualBoundingBoxLeft;
                    const actualRight = metrics.actualBoundingBoxRight;
                    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
                    const yOffset = -metrics.actualBoundingBoxAscent;

                    // バウンディングボックスの4つの角（回転前）
                    // 実際のグリフの左端と右端を使用
                    const boxLeft = position.x - actualLeft;
                    const boxRight = position.x + actualRight;
                    const boxY = position.y + yOffset;
                    const corners = [
                        [boxLeft, boxY],                        // 左上
                        [boxRight, boxY],                       // 右上
                        [boxRight, boxY + textHeight],          // 右下
                        [boxLeft, boxY + textHeight]            // 左下
                    ];

                    // 各角を回転中心まわりに回転変換
                    corners.forEach(([x, y]) => {
                        // 回転中心を原点とする座標系に変換
                        const dx = x - textCenterX;
                        const dy = y - textCenterY;

                        // 回転変換を適用
                        const rotatedX = dx * cos - dy * sin + textCenterX;
                        const rotatedY = dx * sin + dy * cos + textCenterY;

                        // AABBの範囲を更新
                        aabbMinX = Math.min(aabbMinX, rotatedX);
                        aabbMinY = Math.min(aabbMinY, rotatedY);
                        aabbMaxX = Math.max(aabbMaxX, rotatedX);
                        aabbMaxY = Math.max(aabbMaxY, rotatedY);
                    });
                }
            });

            // 回転後のAABBの左上端が目標位置になるようにオフセットを計算
            const offsetX = targetX - aabbMinX;
            const offsetY = targetY - aabbMinY;

            // オフセットを適用して、回転中心と各行の位置を調整
            textCenterX += offsetX;
            textCenterY += offsetY;

            // 現在の状態を保存
            ctx.save();

            // 回転の中心点に移動
            ctx.translate(textCenterX, textCenterY);

            // 回転を適用
            ctx.rotate(rotationRadians);

            // 回転の中心点を原点に戻す
            ctx.translate(-textCenterX, -textCenterY);

            // 各行を描画（オフセットを適用）
            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    ctx.fillText(position.text, position.x + offsetX, position.y + offsetY);
                }
            });

            // 状態を復元
            ctx.restore();
        } else {
            // 回転なしの場合は通常通り描画
            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    ctx.fillText(position.text, position.x, position.y);
                }
            });
        }
    }

    /**
     * フォントスタイル文字列を構築
     * @param layout テキストレイアウト情報
     * @returns フォントスタイル文字列
     */
    private buildFontStyle(layout: TextLayout, strap: StrapDimensions): string {
        const parts: string[] = [];

        // イタリック
        if (layout.isItalic) {
            parts.push('italic');
        }

        // 太字
        if (layout.isBold) {
            parts.push('bold');
        }

        // フォントサイズとファミリー
        // pt → px 変換（ズームレベルに応じて自動調整）
        const basePtToPx = 96 / 72;
        const currentDpi = (strap.canvasWidth / strap.widthMm) * 25.4;
        const scaledFontSize = layout.fontSize * basePtToPx * (currentDpi / 96);
        parts.push(`${scaledFontSize}px`);
        parts.push(layout.fontFamily || 'Arial');

        return parts.join(' ');
    }

    /**
     * Canvasをクリア
     * @param canvas Canvas要素
     */
    clearCanvas(canvas: HTMLCanvasElement): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Canvas 2Dコンテキストの取得に失敗しました');
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    /**
     * Canvasが初期化されているか確認
     * @param canvas Canvas要素
     * @returns 初期化されている場合true
     */
    isInitialized(canvas: HTMLCanvasElement): boolean {
        return canvas !== null && canvas.getContext('2d') !== null;
    }
}

// Reactで使用するためクラスを直接エクスポート
export {CanvasInterop as CanvasInteropService};
